import React from "react"
import { useFormik } from "formik"
import * as yup from "yup"

import Button from "components/Buttons/Button"
import CTAFormSpinner from "components/CTAForm/CTAFormSpinner"

import GateForContentFirstStage from "components/GateForContent/GateForContentFirstStage"
import GateForContentStageHeading from "components/GateForContent/GateForContentStageHeading"
import GateForContentStageNotice from "components/GateForContent/GateForContentStageNotice"
import GateForContentStageTitle from "components/GateForContent/GateForContentStageTitle"
import GateForContentForm from "components/GateForContent/GateForContentForm"
import GateForContentFormField from "components/GateForContent/GateForContentFormField"
import GateForContentConfirmation from "components/GateForContent/GateForContentConfirmation"
import GateForContentConfirmationLink from "components/GateForContent/GateForContentConfirmationLink"
import GateForContentButton from "components/GateForContent/GateForContentButton"
import GateForContentFormFieldWrapper from "components/GateForContent/GateForContentFormFieldWrapper"
import GateForContentFormFieldName from "components/GateForContent/GateForContentFormFieldName"
import GateForContentThirdStageText from "components/GateForContent/GateForContentThirdStageText"
import GateForContentThirdStageTitle from "components/GateForContent/GateForContentThirdStageTitle"
import GateForContentButtonWrapper from "components/GateForContent/GateForContentButtonWrapper"

import GateForContentImage from "components/GateForContent/GateForContentImage"
import GateForContentSecondStage from "components/GateForContent/GateForContentSecondStage"
import GateForContentStages from "components/GateForContent/GateForContentStages"
import GateForContentThirdStage from "components/GateForContent/GateForContentThirdStage"
import GateForContentWrapper from "components/GateForContent/GateForContentWrapper"
import FormFinalImage from "components/Icons/FormFinalImage"
import PropTypes from "prop-types"
import DEFAULT_FORM_ERROR from "utils/defaultFormError"
import { handleReCaptchaVerify } from "utils/handleReCaptchaVerify"
import { MAX_INPUT_LENGTH } from "utils/inputLimits"
import pushGTMEvent from "utils/pushGTMEvent"
import { noHTMLTest } from "utils/noHTMLRegexp"
import { isTemporaryEmail, isNonBusinessEmail } from "utils/temporaryEmailsList"

const validationSchema = yup.object({
  name: yup
    .string("Full Name")
    .required("Name is required")
    .test(
      "no-html",
      "Name should not contain HTML tags or other disallowed characters",
      noHTMLTest,
    ),
  email: yup
    .string("Email")
    .email("Enter a valid email")
    .required("Email is required")
    .test(
      "no-temp-email",
      "Temporary email addresses are not allowed",
      (value) => !isTemporaryEmail(value),
    )
    .test(
      "no-non-business-email",
      "Non-business email addresses are not allowed",
      (value) => !isNonBusinessEmail(value),
    )
    .test(
      "no-html",
      "Name should not contain HTML tags or other disallowed characters",
      noHTMLTest,
    ),
})

const GateForContent = ({
  buttonText,
  title,
  titleTag,
  sectionId,
  confirmationLink,
  confirmationLinkText,
  confirmationText,
  submitLink,
  finalStageText,
  finalStageBtnText,
  formFieldNameTitle,
  formFieldEmailTitle,
  formPlaceholderEmail,
  formPlaceholderName,
  formId,
  image,
  notice,
  recaptcha,
  finalStageTitle,
}) => {
  const formik = useFormik({
    initialValues: {
      formId,
      name: "",
      email: "",
    },
    isInitialValid: false,
    validationSchema,
  })

  const [submittedEmail, setSubmittedEmail] = React.useState("")
  const [prevFormState, setPrevFormState] = React.useState(formik.initialValues)
  const [sending, setSending] = React.useState(false)
  const [showFinal, setShowFinal] = React.useState(false)
  const [formHasError, setFormHasError] = React.useState(false)
  const [recaptchaRating, setRecaptchaRating] = React.useState(null)

  const showFinalHandler = () => {
    setShowFinal(!showFinal)
  }

  const handleFormSubmit = (success = true) => {
    if (success) {
      setSubmittedEmail(formik.values.email)
      formik.resetForm()
      showFinalHandler()
    } else {
      formik.resetForm()
      setFormHasError(true)
      showFinalHandler()
    }
  }

  return (
    <GateForContentWrapper>
      <GateForContentStages>
        {/* FIRST STAGE  */}
        <GateForContentFirstStage
          id="gateForContentForm"
          formName="gateForContentForm"
          endpoint={submitLink}
          setSending={setSending}
          recaptchaValue={recaptchaRating}
          fieldMapping={{
            name: "firstname",
            lastname: "lastname",
            email: "email",
            recaptchaValue: "recaptchaValue",
          }}
          formValues={formik.values}
          preSubmit={() => handleReCaptchaVerify(recaptcha, setRecaptchaRating)}
          postSubmit={handleFormSubmit}
        >
          <GateForContentStageHeading>
            <GateForContentStageNotice>{notice}</GateForContentStageNotice>
            <GateForContentStageTitle as={titleTag}>{title}</GateForContentStageTitle>
          </GateForContentStageHeading>
          <GateForContentForm>
            <GateForContentFormField type="hidden" key="formId" name="formId" />
            <GateForContentFormFieldWrapper>
              <GateForContentFormFieldName>
                {formFieldNameTitle}
                <span>*</span>
              </GateForContentFormFieldName>
              <GateForContentFormField
                error={formik.errors.name}
                onChange={formik.handleChange}
                value={formik.values.name}
                touched={formik.touched?.name}
                maxLength={MAX_INPUT_LENGTH}
                type="text"
                key="name"
                name="name"
                required
                onFocus={() => {
                  formik.setFieldTouched("name", true, true)
                }}
                onBlur={() => {
                  if (formik.values.name !== "" && prevFormState.name !== formik.values.name)
                    setPrevFormState(formik.values)
                }}
                placeholder={formPlaceholderName}
              />
            </GateForContentFormFieldWrapper>
            <GateForContentFormFieldWrapper>
              <GateForContentFormFieldName>
                {formFieldEmailTitle}
                <span>*</span>
              </GateForContentFormFieldName>
              <GateForContentFormField
                error={formik.errors.email}
                onChange={formik.handleChange}
                value={formik.values.email || ""}
                touched={formik.touched?.email}
                type="email"
                key="email"
                name="email"
                required
                onFocus={() => {
                  formik.setFieldTouched("email", true, true)
                }}
                onBlur={() => {
                  if (formik.values.email !== "" && prevFormState.email !== formik.values.email) {
                    setPrevFormState(formik.values)
                    pushGTMEvent("email_field_touched", "gateForContentForm")
                  }
                }}
                placeholder={formPlaceholderEmail}
              />
            </GateForContentFormFieldWrapper>
          </GateForContentForm>
          <GateForContentConfirmation>
            {confirmationText}{" "}
            <GateForContentConfirmationLink to={confirmationLink} target="_blank">
              {confirmationLinkText}
            </GateForContentConfirmationLink>
          </GateForContentConfirmation>
          <GateForContentButtonWrapper>
            <GateForContentButton
              variant="contained"
              id="gate-for-content-form-submit"
              type="submit"
            >
              {buttonText}
            </GateForContentButton>
          </GateForContentButtonWrapper>
        </GateForContentFirstStage>

        {/* SECOND STAGE  */}
        {sending && (
          <GateForContentSecondStage>
            <CTAFormSpinner />
          </GateForContentSecondStage>
        )}

        {/* THIRD STAGE */}
        {showFinal && (
          <GateForContentThirdStage>
            {!formHasError && <FormFinalImage variant="small" />}
            {!formHasError && (
              <GateForContentThirdStageTitle>{finalStageTitle}</GateForContentThirdStageTitle>
            )}
            <GateForContentThirdStageText>
              {formHasError
                ? DEFAULT_FORM_ERROR
                : ` We are always happy to share our expertise with you! The link to the content will be
              sent in a few minutes to your email, ${submittedEmail}.
              If you have any questions,
              don't hesitate to contact us.`}
            </GateForContentThirdStageText>
            <Button variant="contained" onClick={showFinalHandler}>
              {finalStageBtnText}
            </Button>
          </GateForContentThirdStage>
        )}
      </GateForContentStages>
      <GateForContentImage objectFit="contain" {...image} />
    </GateForContentWrapper>
  )
}

export default GateForContent

GateForContent.propTypes = {
  buttonText: PropTypes.string.isRequired,
  recaptcha: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  titleTag: PropTypes.string.isRequired,
  sectionId: PropTypes.string.isRequired,
  confirmationLink: PropTypes.string.isRequired,
  confirmationLinkText: PropTypes.string.isRequired,
  confirmationText: PropTypes.string.isRequired,
  submitLink: PropTypes.string.isRequired,
  finalStageText: PropTypes.any.isRequired,
  finalStageBtnText: PropTypes.string.isRequired,
  formPlaceholderEmail: PropTypes.string.isRequired,
  formPlaceholderName: PropTypes.string.isRequired,
  formId: PropTypes.string.isRequired,
  image: PropTypes.object.isRequired,
  notice: PropTypes.string.isRequired,
  linkToFile: PropTypes.string.isRequired,
  finalStageTitle: PropTypes.string.isRequired,
  formFieldNameTitle: PropTypes.string.isRequired,
  formFieldEmailTitle: PropTypes.string.isRequired,
}
